/* ==========================================================================
   Drywell theme scripts
   GSAP + ScrollTrigger + SplitText + Lenis are loaded before this file.
   ========================================================================== */
(() => {
  'use strict';

  const D = window.Drywell || {};
  const html = document.documentElement;
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  const hasGsap = typeof window.gsap !== 'undefined' && typeof window.ScrollTrigger !== 'undefined';
  const motion = hasGsap && !reduced;

  if (reduced) html.classList.add('reduced-motion');
  if (hasGsap) {
    gsap.registerPlugin(ScrollTrigger);
    if (window.SplitText) gsap.registerPlugin(SplitText);
  }

  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));
  const clamp = (v, a, b) => Math.min(b, Math.max(a, v));
  const lerp = (a, b, t) => a + (b - a) * t;
  const fontsReady = document.fonts ? document.fonts.ready : Promise.resolve();

  /* ------------------------------------------------------------------
     Money
  ------------------------------------------------------------------ */
  function formatMoney(cents, format = D.moneyFormat || '£{{amount}}') {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    cents = Number(cents) || 0;
    const fmt = (n, p = 2, t = ',', d = '.') => {
      const num = (n / 100).toFixed(p);
      const [whole, dec] = num.split('.');
      return whole.replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${t}`) + (dec ? d + dec : '');
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals': return fmt(cents, 0);
        case 'amount_with_comma_separator': return fmt(cents, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return fmt(cents, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return fmt(cents, 2, "'", '.');
        default: return fmt(cents, 2);
      }
    });
  }

  /* ------------------------------------------------------------------
     Smooth scroll
  ------------------------------------------------------------------ */
  let lenis = null;
  function initLenis() {
    if (!motion || !D.settings?.smoothScroll || typeof window.Lenis === 'undefined') return;
    if (window.Shopify && window.Shopify.designMode) return; // keep the editor predictable
    lenis = new Lenis({ lerp: 0.09, smoothWheel: true, wheelMultiplier: 1 });
    lenis.on('scroll', ScrollTrigger.update);
    gsap.ticker.add((t) => lenis.raf(t * 1000));
    gsap.ticker.lagSmoothing(0);
    $$('a[href^="#"]').forEach((a) => a.addEventListener('click', (e) => {
      const target = $(a.getAttribute('href'));
      if (target) { e.preventDefault(); lenis.scrollTo(target, { offset: -80 }); }
    }));
  }

  /* ------------------------------------------------------------------
     Preloader — resolves when the page is ready to be shown
  ------------------------------------------------------------------ */
  function runPreloader() {
    const el = $('[data-preloader]');
    if (!el) return Promise.resolve();
    let seen = false;
    try { seen = sessionStorage.getItem('dw-intro') === '1'; } catch (e) {}
    if (!motion || seen || (window.Shopify && window.Shopify.designMode)) {
      el.remove();
      return Promise.resolve();
    }
    try { sessionStorage.setItem('dw-intro', '1'); } catch (e) {}
    lenis && lenis.stop();
    const drop = $('.preloader__drop', el);
    const ripples = $$('.preloader__ripple', el);
    const word = $('.preloader__word', el);
    return new Promise((resolve) => {
      const tl = gsap.timeline({
        onComplete: () => { el.remove(); lenis && lenis.start(); }
      });
      tl.from(drop, { y: -140, scaleY: 1.4, duration: .7, ease: 'power2.in' })
        .to(drop, { scaleY: .4, scaleX: 1.5, y: 40, opacity: 0, duration: .18, ease: 'power1.out' })
        .fromTo(ripples, { scale: .2, opacity: 1 }, { scale: 3.2, opacity: 0, duration: 1.1, stagger: .16, ease: 'power2.out' }, '<')
        .from(word, { yPercent: 110, duration: .8, ease: 'expo.out' }, '<.1')
        .to(el, { clipPath: 'inset(0 0 100% 0)', duration: .9, ease: 'expo.inOut', onStart: resolve }, '+=.25');
    });
  }

  /* ------------------------------------------------------------------
     Cursor + magnetic buttons
  ------------------------------------------------------------------ */
  function initCursor() {
    const cursor = $('.cursor');
    if (!cursor || !finePointer || reduced) { cursor && cursor.remove(); return; }
    const label = $('.cursor__label', cursor);
    let x = innerWidth / 2, y = innerHeight / 2, cx = x, cy = y;
    addEventListener('pointermove', (e) => { x = e.clientX; y = e.clientY; cursor.classList.add('is-visible'); }, { passive: true });
    document.addEventListener('pointerleave', () => cursor.classList.remove('is-visible'));
    const tick = () => {
      cx = lerp(cx, x, .2); cy = lerp(cy, y, .2);
      cursor.style.transform = `translate3d(${cx}px, ${cy}px, 0)`;
      requestAnimationFrame(tick);
    };
    tick();
    document.addEventListener('pointerover', (e) => {
      const t = e.target.closest('[data-cursor]');
      if (t) { label.textContent = t.dataset.cursor; cursor.classList.add('is-big'); }
    });
    document.addEventListener('pointerout', (e) => {
      const t = e.target.closest('[data-cursor]');
      if (t && !t.contains(e.relatedTarget)) cursor.classList.remove('is-big');
    });
  }

  function initMagnetic(root = document) {
    if (!finePointer || !motion) return;
    $$('[data-magnetic]', root).forEach((el) => {
      if (el.dataset.magBound) return;
      el.dataset.magBound = '1';
      const xTo = gsap.quickTo(el, 'x', { duration: .6, ease: 'elastic.out(1, .4)' });
      const yTo = gsap.quickTo(el, 'y', { duration: .6, ease: 'elastic.out(1, .4)' });
      el.addEventListener('pointermove', (e) => {
        const r = el.getBoundingClientRect();
        xTo((e.clientX - (r.left + r.width / 2)) * .35);
        yTo((e.clientY - (r.top + r.height / 2)) * .45);
      });
      el.addEventListener('pointerleave', () => { xTo(0); yTo(0); });
    });
  }

  /* ------------------------------------------------------------------
     Header + menu
  ------------------------------------------------------------------ */
  function initHeader() {
    const header = $('[data-header]');
    if (!header) return;
    const hero = $('[data-hero]');
    let last = 0;
    const update = () => {
      const y = window.scrollY;
      const heroEnd = hero ? hero.offsetTop + hero.offsetHeight - 80 : 10;
      header.classList.toggle('is-solid', y > heroEnd || !hero);
      header.classList.toggle('is-hidden', y > 400 && y > last && !header.contains(document.activeElement));
      last = y;
    };
    addEventListener('scroll', update, { passive: true });
    update();

    const overlay = $('[data-menu]');
    const open = $('[data-menu-open]');
    const close = $('[data-menu-close]');
    if (overlay && open) {
      const set = (state) => {
        overlay.classList.toggle('is-open', state);
        overlay.setAttribute('aria-hidden', String(!state));
        open.setAttribute('aria-expanded', String(state));
        state ? lenis?.stop() : lenis?.start();
        document.body.style.overflow = state ? 'hidden' : '';
        if (state) close?.focus(); else open.focus();
      };
      open.addEventListener('click', () => set(true));
      close?.addEventListener('click', () => set(false));
      overlay.addEventListener('keydown', (e) => { if (e.key === 'Escape') set(false); });
    }
  }

  /* ------------------------------------------------------------------
     Generic reveals: split headings + fade-ups
  ------------------------------------------------------------------ */
  function initReveals(root) {
    if (!motion) return;
    if (window.SplitText) {
      $$('[data-split]', root).forEach((el) => {
        if (el.closest('[data-hero]')) return;
        SplitText.create(el, {
          type: 'lines', mask: 'lines', autoSplit: true, linesClass: 'split-line',
          onSplit: (self) => gsap.from(self.lines, {
            yPercent: 115, rotate: 2, duration: 1.15, stagger: .09, ease: 'expo.out',
            scrollTrigger: { trigger: el, start: 'top 88%', once: true }
          })
        });
      });
    }
    $$('[data-reveal]', root).forEach((el) => {
      gsap.fromTo(el, { opacity: 0, y: 48 }, {
        opacity: 1, y: 0, duration: 1.1, ease: 'expo.out', delay: Number(el.dataset.reveal) || 0,
        scrollTrigger: { trigger: el, start: 'top 90%', once: true }
      });
    });
  }

  /* ------------------------------------------------------------------
     In-view video playback
  ------------------------------------------------------------------ */
  const io = 'IntersectionObserver' in window ? new IntersectionObserver((entries) => {
    entries.forEach(({ target, isIntersecting }) => {
      if (isIntersecting) { const p = target.play(); p && p.catch(() => {}); }
      else target.pause();
    });
  }, { rootMargin: '150px 0px' }) : null;

  function initAutoplay(root) {
    $$('video[data-autoplay]', root).forEach((v) => {
      v.muted = true; v.loop = true;
      if (reduced) { v.removeAttribute('autoplay'); return; }
      if (io) io.observe(v); else v.play().catch(() => {});
    });
  }

  /* ------------------------------------------------------------------
     HERO — fogged window you can wipe, then it shrinks into a pane
  ------------------------------------------------------------------ */
  function FogGlass(canvas, video) {
    const ctx = canvas.getContext('2d');
    const mask = document.createElement('canvas');
    const mctx = mask.getContext('2d');
    const tiny = document.createElement('canvas');
    const tctx = tiny.getContext('2d');
    const beads = document.createElement('canvas');
    const brush = document.createElement('canvas');
    const SCALE = 0.5;
    const LIFE = 7000, HOLD = 2600;
    let w = 0, h = 0, points = [], running = false, visible = true, last = null, raf = 0;
    const ink = getComputedStyle(html).getPropertyValue('--ink-rgb').trim() || '29, 43, 51';
    const fog = getComputedStyle(html).getPropertyValue('--fog-rgb').trim() || '232, 238, 240';

    function makeBrush(r) {
      brush.width = brush.height = r * 2;
      const b = brush.getContext('2d');
      const g = b.createRadialGradient(r, r, 0, r, r, r);
      g.addColorStop(0, 'rgba(0,0,0,1)'); g.addColorStop(.55, 'rgba(0,0,0,.9)'); g.addColorStop(1, 'rgba(0,0,0,0)');
      b.fillStyle = g; b.fillRect(0, 0, r * 2, r * 2);
    }
    function makeBeads() {
      beads.width = w; beads.height = h;
      const b = beads.getContext('2d');
      const count = Math.round((w * h) / 260);
      for (let i = 0; i < count; i++) {
        const x = Math.random() * w, y = Math.random() * h;
        const r = Math.random() < .93 ? Math.random() * 1.4 + .3 : Math.random() * 3.5 + 1.5;
        b.fillStyle = `rgba(${fog}, ${.05 + Math.random() * .12})`;
        b.beginPath(); b.arc(x, y, r, 0, Math.PI * 2); b.fill();
        if (r > 1.5) {
          b.fillStyle = `rgba(255,255,255,${.18 + Math.random() * .2})`;
          b.beginPath(); b.arc(x - r * .35, y - r * .35, r * .35, 0, Math.PI * 2); b.fill();
        }
      }
    }
    function resize() {
      const r = canvas.getBoundingClientRect();
      w = canvas.width = mask.width = Math.max(2, Math.round(r.width * SCALE));
      h = canvas.height = mask.height = Math.max(2, Math.round(r.height * SCALE));
      tiny.width = Math.max(16, Math.round(w / 22));
      tiny.height = Math.max(16, Math.round(h / 22));
      makeBrush(Math.round(Math.max(w, h) * .055));
      makeBeads();
    }
    function coverDraw(c, img, cw, ch) {
      const vw = img.videoWidth, vh = img.videoHeight;
      if (!vw) return false;
      const s = Math.max(cw / vw, ch / vh);
      const sw = cw / s, sh = ch / s;
      c.drawImage(img, (vw - sw) / 2, (vh - sh) / 2, sw, sh, 0, 0, cw, ch);
      return true;
    }
    function addPoint(x, y) {
      const now = performance.now();
      if (last) {
        const dx = x - last.x, dy = y - last.y;
        const dist = Math.hypot(dx, dy);
        const step = brush.width * .22;
        for (let d = step; d < dist; d += step) points.push({ x: last.x + dx * d / dist, y: last.y + dy * d / dist, t: now });
      }
      points.push({ x, y, t: now });
      last = { x, y };
      if (points.length > 1400) points.splice(0, points.length - 1400);
    }
    function frame() {
      raf = requestAnimationFrame(frame);
      if (!visible) return;
      const now = performance.now();
      // blurred, tinted copy of the video = fog
      ctx.globalCompositeOperation = 'source-over';
      ctx.globalAlpha = 1;
      if (video.readyState >= 2 && coverDraw(tctx, video, tiny.width, tiny.height)) {
        ctx.imageSmoothingEnabled = true; ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(tiny, 0, 0, w, h);
      } else {
        ctx.fillStyle = `rgb(${ink})`; ctx.fillRect(0, 0, w, h);
      }
      ctx.fillStyle = `rgba(${ink}, .46)`; ctx.fillRect(0, 0, w, h);
      ctx.fillStyle = `rgba(${fog}, .09)`; ctx.fillRect(0, 0, w, h);
      ctx.drawImage(beads, 0, 0);
      // wiped streaks
      mctx.clearRect(0, 0, w, h);
      points = points.filter((p) => now - p.t < LIFE);
      const half = brush.width / 2;
      for (const p of points) {
        const age = now - p.t;
        mctx.globalAlpha = age < HOLD ? 1 : 1 - (age - HOLD) / (LIFE - HOLD);
        mctx.drawImage(brush, p.x - half, p.y - half);
      }
      mctx.globalAlpha = 1;
      ctx.globalCompositeOperation = 'destination-out';
      ctx.drawImage(mask, 0, 0);
      ctx.globalCompositeOperation = 'source-over';
    }
    const toLocal = (e) => {
      const r = canvas.getBoundingClientRect();
      return [(e.clientX - r.left) * SCALE, (e.clientY - r.top) * SCALE];
    };
    canvas.addEventListener('pointermove', (e) => addPoint(...toLocal(e)), { passive: true });
    canvas.addEventListener('pointerdown', (e) => { last = null; addPoint(...toLocal(e)); }, { passive: true });
    canvas.addEventListener('pointerleave', () => { last = null; });

    return {
      start() { if (running) return; running = true; resize(); frame(); addEventListener('resize', resize); },
      stop() { running = false; cancelAnimationFrame(raf); removeEventListener('resize', resize); },
      setVisible(v) { visible = v; },
      // scripted wipe so visitors see what the glass does
      demo() {
        const path = { p: 0 };
        last = null;
        return gsap.to(path, {
          p: 1, duration: 1.6, ease: 'power2.inOut',
          onUpdate: () => {
            const t = path.p;
            addPoint(w * (.52 + .36 * t), h * (.32 + Math.sin(t * Math.PI * 2.2) * .08 + t * .16));
          },
          onComplete: () => { last = null; }
        });
      }
    };
  }

  function initHero(section) {
    const video = $('.hero__video', section);
    const canvas = $('[data-fog]', section);
    const frame = $('.hero__frame', section);
    const title = $('.hero__title', section);
    const copy = $('.hero__copy', section);
    const hint = $('.hero__hint', section);
    if (video) {
      video.muted = true;
      if (reduced) { video.removeAttribute('autoplay'); video.pause(); }
      else { const p = video.play(); p && p.catch(() => {}); }
    }

    if (!motion) return;

    let fog = null;
    if (canvas && video && canvas.getContext) {
      fog = FogGlass(canvas, video);
      fog.start();
      ScrollTrigger.create({
        trigger: section, start: 'top top', end: 'bottom top',
        onToggle: (self) => fog.setVisible(self.isActive)
      });
    }

    // intro (after preloader)
    let split = null;
    if (title && window.SplitText) split = SplitText.create(title, { type: 'lines,chars', mask: 'lines', linesClass: 'split-line' });
    const sub = $$('.hero__sub, .hero__copy .btn', section);
    gsap.set(sub, { opacity: 0, y: 30 });
    if (split) gsap.set(split.chars, { yPercent: 120, rotate: 6 });
    const intro = () => {
      const tl = gsap.timeline();
      tl.fromTo(video, { scale: 1.35 }, { scale: 1.12, duration: 2.4, ease: 'expo.out' }, 0);
      if (split) tl.to(split.chars, { yPercent: 0, rotate: 0, duration: 1.2, stagger: .022, ease: 'expo.out' }, .1);
      tl.to(sub, { opacity: 1, y: 0, duration: 1, stagger: .1, ease: 'expo.out' }, .55);
      if (hint) tl.from(hint, { opacity: 0, x: 20, duration: .8 }, 1);
      if (fog) tl.add(() => fog.demo(), .9);
      return tl;
    };

    // scroll: fog lifts, the view shrinks into a window pane with glazing bars
    const mm = gsap.matchMedia();
    mm.add({ mobile: '(max-width: 749px)', desktop: '(min-width: 750px)' }, (c) => {
      const inset = c.conditions.mobile ? '20% 6% 20% 6%' : '15% 24% 15% 24%';
      const radius = getComputedStyle(html).getPropertyValue('--radius-pane').trim() || '22px';
      const tl = gsap.timeline({
        scrollTrigger: { trigger: section, start: 'top top', end: '+=130%', scrub: 1, pin: $('.hero__pin', section), anticipatePin: 1 }
      });
      tl.to(canvas, { opacity: 0, ease: 'none', duration: .35 }, 0)
        .set(canvas, { pointerEvents: 'none' }, .3)
        .to(copy, { yPercent: -30, opacity: 0, ease: 'none', duration: .3 }, 0)
        .to(hint, { opacity: 0, duration: .1 }, 0)
        .fromTo(frame, { clipPath: 'inset(0% 0% 0% 0% round 0px)' }, { clipPath: `inset(${inset} round ${radius})`, ease: 'power2.inOut', duration: .7 }, .25)
        .to(video, { scale: 1, ease: 'none', duration: 1 }, 0)
        .to($('.hero__mullion .v', section), { scaleY: 1, ease: 'power3.inOut', duration: .3 }, .6)
        .to($('.hero__mullion .h', section), { scaleX: 1, ease: 'power3.inOut', duration: .3 }, .66)
        .to($$('.hero__after p', section), { opacity: 1, y: 0, stagger: .08, duration: .25 }, .72);
      gsap.set($$('.hero__after p', section), { y: 30 });
    });

    return intro;
  }

  /* ------------------------------------------------------------------
     Marquee driven by scroll velocity
  ------------------------------------------------------------------ */
  function initMarquee(section) {
    const rows = $$('.marquee__row', section);
    if (!rows.length) return;
    if (!motion) return;
    const tweens = rows.map((row, i) => {
      const dir = i % 2 ? 1 : -1;
      gsap.set(row, { xPercent: dir === 1 ? -50 : 0 });
      return gsap.to(row, { xPercent: dir === 1 ? 0 : -50, ease: 'none', duration: Number(section.dataset.speed) || 28, repeat: -1 });
    });
    const skewTo = rows.map((r) => gsap.quickTo(r, 'skewX', { duration: .5, ease: 'power3' }));
    let dirSign = 1;
    ScrollTrigger.create({
      trigger: section, start: 'top bottom', end: 'bottom top',
      onUpdate: (self) => {
        const v = self.getVelocity();
        dirSign = self.direction;
        const boost = clamp(1 + Math.abs(v) / 250, 1, 7);
        tweens.forEach((t) => {
          gsap.to(t, { timeScale: boost * dirSign, duration: .2, overwrite: true });
          gsap.to(t, { timeScale: dirSign, duration: 1.2, delay: .2, ease: 'power2.out' });
        });
        skewTo.forEach((s) => s(clamp(v / -120, -12, 12)));
        gsap.delayedCall(.15, () => skewTo.forEach((s) => s(0)));
      }
    });
  }

  /* ------------------------------------------------------------------
     Scroll-scrubbed video + humidity dial
  ------------------------------------------------------------------ */
  function initScrub(section) {
    const video = $('video', section);
    const steps = $$('.scrub__step', section);
    const num = $('[data-dial-num]', section);
    const dial = $('.dial', section);
    const fill = $('.dial__fill', section);
    const bar = $('.scrub__progress span', section);
    const from = Number(section.dataset.from) || 78;
    const to = Number(section.dataset.to) || 48;
    const C = 2 * Math.PI * 90;
    const ARC = C * .75;

    const setDial = (v) => {
      if (num) num.textContent = Math.round(v);
      if (fill) fill.setAttribute('stroke-dasharray', `${(ARC * v / 100).toFixed(1)} ${C.toFixed(1)}`);
      dial && dial.classList.toggle('is-dry', v <= 60);
    };
    const setStep = (p) => {
      const idx = Math.min(steps.length - 1, Math.floor(p * steps.length));
      steps.forEach((s, i) => s.classList.toggle('is-active', i === idx));
    };
    setDial(from); setStep(0);

    if (!video) return;
    video.muted = true;

    if (!motion) {
      video.loop = true; video.setAttribute('data-autoplay', '');
      initAutoplay(section);
      setDial(to); steps.forEach((s, i) => s.classList.toggle('is-active', i === steps.length - 1));
      return;
    }

    // iOS needs a play/pause before it will seek
    const unlock = () => { const p = video.play(); p && p.then(() => video.pause()).catch(() => {}); };
    addEventListener('touchstart', unlock, { once: true, passive: true });
    video.pause();

    let target = 0, current = 0, duration = 0;
    const ready = () => { duration = video.duration || 0; };
    if (video.readyState >= 1) ready(); else video.addEventListener('loadedmetadata', ready, { once: true });

    const tick = () => {
      if (duration) {
        current = lerp(current, target, .18);
        if (Math.abs(video.currentTime - current) > .015 && !video.seeking) {
          try { video.currentTime = current; } catch (e) {}
        }
      }
    };
    gsap.ticker.add(tick);

    ScrollTrigger.create({
      trigger: section, start: 'top top', end: () => '+=' + Math.round(innerHeight * 2.6),
      pin: $('.scrub__pin', section), scrub: true, anticipatePin: 1,
      onUpdate: (self) => {
        const p = self.progress;
        target = duration ? p * (duration - .06) : 0;
        setDial(lerp(from, to, p));
        setStep(p);
        if (bar) bar.style.transform = `scaleX(${p})`;
      }
    });
    gsap.from($('.scrub__pane', section), {
      clipPath: 'inset(18% 18% 18% 18% round 120px)', scale: .9, ease: 'none',
      scrollTrigger: { trigger: section, start: 'top bottom', end: 'top top', scrub: true }
    });
    return () => gsap.ticker.remove(tick);
  }

  /* ------------------------------------------------------------------
     Pinned horizontal walkthrough
  ------------------------------------------------------------------ */
  function initHScroll(section) {
    if (!motion) return;
    const track = $('.hscroll__track', section);
    const pin = $('.hscroll__pin', section);
    const bar = $('.hscroll__bar span', section);
    if (!track) return;
    const dist = () => Math.max(0, track.scrollWidth - innerWidth);
    const tween = gsap.to(track, {
      x: () => -dist(), ease: 'none',
      scrollTrigger: {
        trigger: section, start: 'top top', end: () => '+=' + dist(), pin, scrub: 1, invalidateOnRefresh: true, anticipatePin: 1,
        onUpdate: (self) => bar && (bar.style.transform = `scaleX(${self.progress})`)
      }
    });
    $$('.hscroll__media > *', section).forEach((m) => {
      gsap.fromTo(m, { xPercent: -10, scale: 1.15 }, {
        xPercent: 10, ease: 'none',
        scrollTrigger: { trigger: m.parentElement, containerAnimation: tween, start: 'left right', end: 'right left', scrub: true }
      });
    });
    $$('.hscroll__panel:not(.hscroll__panel--intro)', section).forEach((p) => {
      gsap.from(p, {
        rotate: 4, yPercent: 8, ease: 'none',
        scrollTrigger: { trigger: p, containerAnimation: tween, start: 'left right', end: 'center center', scrub: true }
      });
    });
  }

  /* ------------------------------------------------------------------
     Video grid: columns drift at different speeds, videos move inside
  ------------------------------------------------------------------ */
  function initVideoGrid(section) {
    initAutoplay(section);
    if (!motion) return;
    $$('.vgrid__col', section).forEach((col) => {
      const s = Number(col.dataset.speed) || 0;
      gsap.fromTo(col, { yPercent: s }, {
        yPercent: -s, ease: 'none',
        scrollTrigger: { trigger: section, start: 'top bottom', end: 'bottom top', scrub: true }
      });
    });
    $$('.tile', section).forEach((tile) => {
      const v = $('video', tile);
      gsap.fromTo(tile, { clipPath: 'inset(100% 0% 0% 0% round 24px)' }, {
        clipPath: 'inset(0% 0% 0% 0% round 0px)', duration: 1.4, ease: 'expo.out',
        scrollTrigger: { trigger: tile, start: 'top 92%', once: true }
      });
      if (v) {
        gsap.fromTo(v, { yPercent: -8, scale: 1.25 }, {
          yPercent: 8, scale: 1, ease: 'none',
          scrollTrigger: { trigger: tile, start: 'top bottom', end: 'bottom top', scrub: true }
        });
      }
    });
    const cols = $$('.vgrid__col', section);
    const skew = cols.map((c) => gsap.quickTo(c, 'skewY', { duration: .6, ease: 'power3' }));
    ScrollTrigger.create({
      trigger: section, start: 'top bottom', end: 'bottom top',
      onUpdate: (self) => {
        const v = clamp(self.getVelocity() / -400, -4, 4);
        skew.forEach((s, i) => s(v * (i % 2 ? -1 : 1)));
        gsap.delayedCall(.2, () => skew.forEach((s) => s(0)));
      }
    });
  }

  /* ------------------------------------------------------------------
     Finale: video seen through the wordmark, then we zoom through it
  ------------------------------------------------------------------ */
  function initFinale(section) {
    initAutoplay(section);
    const maskEl = $('.finale__mask', section);
    const cta = $('.finale__cta', section);
    const shade = $('.finale__shade', section);
    if (!motion) {
      if (maskEl) maskEl.style.display = 'none';
      if (cta) { cta.style.opacity = 1; cta.classList.add('is-live'); }
      if (shade) shade.style.opacity = 1;
      return;
    }
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: section, start: 'top top', end: '+=160%', scrub: 1, pin: $('.finale__pin', section), anticipatePin: 1,
        onUpdate: (self) => cta && cta.classList.toggle('is-live', self.progress > .8)
      }
    });
    tl.fromTo(maskEl, { scale: 1 }, { scale: 14, ease: 'power3.in', duration: 1 }, 0)
      .to(maskEl, { opacity: 0, ease: 'none', duration: .25 }, .6)
      .to(shade, { opacity: 1, duration: .3 }, .7)
      .fromTo(cta, { opacity: 0, y: 60 }, { opacity: 1, y: 0, duration: .3 }, .72);
    const word = $('.finale__word', section);
    if (word && window.SplitText) {
      const s = SplitText.create(word, { type: 'chars' });
      gsap.from(s.chars, {
        yPercent: 60, opacity: 0, stagger: .05, duration: 1.2, ease: 'expo.out',
        scrollTrigger: { trigger: section, start: 'top 70%', once: true }
      });
    }
  }

  /* ------------------------------------------------------------------
     Footer giant wordmark
  ------------------------------------------------------------------ */
  function initFooter(section) {
    const word = $('.footer__word', section);
    if (!word || !motion || !window.SplitText) return;
    const s = SplitText.create(word, { type: 'chars', charsClass: 'char' });
    gsap.from(s.chars, {
      yPercent: 100, stagger: { each: .06, from: 'center' }, duration: 1.3, ease: 'expo.out',
      scrollTrigger: { trigger: word, start: 'top 98%', once: true }
    });
  }

  /* ------------------------------------------------------------------
     Card tilt
  ------------------------------------------------------------------ */
  function initTilt(root) {
    if (!finePointer || !motion) return;
    $$('[data-tilt]', root).forEach((card) => {
      const rx = gsap.quickTo(card, 'rotationX', { duration: .6, ease: 'power3' });
      const ry = gsap.quickTo(card, 'rotationY', { duration: .6, ease: 'power3' });
      gsap.set(card, { transformPerspective: 900 });
      card.addEventListener('pointermove', (e) => {
        const r = card.getBoundingClientRect();
        ry(((e.clientX - r.left) / r.width - .5) * 8);
        rx(((e.clientY - r.top) / r.height - .5) * -8);
      });
      card.addEventListener('pointerleave', () => { rx(0); ry(0); });
    });
  }

  /* ------------------------------------------------------------------
     FAQ accordion
  ------------------------------------------------------------------ */
  function initFaq(root) {
    $$('.faq__q', root).forEach((btn) => {
      btn.addEventListener('click', () => {
        const open = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', String(!open));
        setTimeout(() => ScrollTrigger?.refresh?.(), 600);
      });
    });
  }

  /* ------------------------------------------------------------------
     Product form: variants, quantity, gallery
  ------------------------------------------------------------------ */
  function initProduct(root) {
    $$('[data-product-form]', root).forEach((form) => {
      const scope = form.closest('[data-product]') || root;
      const json = $('[data-product-json]', scope);
      const product = json ? JSON.parse(json.textContent) : null;
      const idInput = $('input[name="id"]', form);
      const btn = $('[data-add]', form);
      const priceEl = $('[data-price]', scope);

      const qtyInput = $('input[name="quantity"]', form);
      $$('[data-qty]', form).forEach((b) => b.addEventListener('click', () => {
        qtyInput.value = Math.max(1, (Number(qtyInput.value) || 1) + Number(b.dataset.qty));
      }));

      const update = () => {
        if (!product) return;
        const chosen = $$('.variant-picker', scope).map((fs) => $('input:checked', fs)?.value);
        const v = product.variants.find((x) => x.options.every((o, i) => o === chosen[i]));
        if (!v) { btn.disabled = true; $('.btn__label', btn).textContent = D.strings.soldOut; return; }
        idInput.value = v.id;
        btn.disabled = !v.available;
        $('.btn__label', btn).textContent = v.available ? D.strings.addToCart : D.strings.soldOut;
        if (priceEl) {
          let h = `<span class="price__now">${formatMoney(v.price)}</span>`;
          if (v.compare_at_price > v.price) h += `<s class="price__was">${formatMoney(v.compare_at_price)}</s>`;
          priceEl.innerHTML = h;
          priceEl.classList.toggle('price--sale', v.compare_at_price > v.price);
        }
        if (v.featured_media) showMedia(scope, v.featured_media.id);
      };
      $$('.variant-picker input', scope).forEach((i) => i.addEventListener('change', update));

      form.addEventListener('submit', async (e) => {
        if (D.settings?.cartType === 'page') return;
        e.preventDefault();
        const label = $('.btn__label', btn);
        btn.disabled = true; label.textContent = D.strings.adding;
        try {
          const res = await fetch(D.routes.cartAdd + '.js', { method: 'POST', headers: { Accept: 'application/json' }, body: new FormData(form) });
          if (!res.ok) throw new Error((await res.json()).description || 'error');
          label.textContent = D.strings.added;
          await Cart.refresh(true);
        } catch (err) {
          label.textContent = err.message && err.message !== 'error' ? err.message : D.strings.error;
        } finally {
          setTimeout(() => { btn.disabled = false; label.textContent = D.strings.addToCart; }, 1600);
        }
      });
    });

    $$('[data-thumb]', root).forEach((t) => t.addEventListener('click', () => {
      showMedia(t.closest('[data-product]') || root, t.dataset.thumb);
    }));
  }
  function showMedia(scope, id) {
    const slides = $$('[data-media]', scope);
    if (!slides.length) return;
    slides.forEach((s) => { s.hidden = String(s.dataset.media) !== String(id); });
    $$('[data-thumb]', scope).forEach((t) => t.setAttribute('aria-current', String(String(t.dataset.thumb) === String(id))));
    const shown = slides.find((s) => !s.hidden);
    if (shown && motion) gsap.fromTo(shown, { clipPath: 'inset(0 0 0 100%)' }, { clipPath: 'inset(0 0 0 0%)', duration: .8, ease: 'expo.out' });
  }

  /* ------------------------------------------------------------------
     Cart drawer
  ------------------------------------------------------------------ */
  const Cart = {
    el: null,
    init() {
      this.el = $('[data-cart-drawer]');
      if (!this.el) return;
      $$('[data-cart-close]', this.el).forEach((b) => b.addEventListener('click', () => this.close()));
      this.el.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.close(); });
      document.addEventListener('click', (e) => {
        const t = e.target.closest('[data-cart-open]');
        if (t && D.settings?.cartType !== 'page') { e.preventDefault(); this.refresh(true); }
      });
      this.el.addEventListener('click', (e) => {
        const b = e.target.closest('[data-line-qty]');
        if (b) this.change(b.dataset.key, Number(b.dataset.lineQty));
      });
    },
    open() {
      this.el.classList.add('is-open'); this.el.setAttribute('aria-hidden', 'false');
      lenis?.stop(); $('.drawer__panel', this.el).focus();
    },
    close() {
      this.el.classList.remove('is-open'); this.el.setAttribute('aria-hidden', 'true');
      lenis?.start();
    },
    async refresh(open) {
      const cart = await (await fetch(D.routes.cart + '.js', { headers: { Accept: 'application/json' } })).json();
      this.render(cart);
      if (open && this.el) this.open();
      return cart;
    },
    async change(key, quantity) {
      try {
        const res = await fetch(D.routes.cartChange + '.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: key, quantity })
        });
        this.render(await res.json());
      } catch (e) { alert(D.strings.error); }
    },
    render(cart) {
      $$('[data-cart-count]').forEach((n) => { n.textContent = cart.item_count; n.dataset.count = cart.item_count; });
      if (!this.el) return;
      $('[data-cart-total]', this.el).textContent = formatMoney(cart.total_price);
      const bar = $('[data-ship-bar]', this.el);
      if (bar) {
        const th = Number(bar.dataset.threshold) || 0;
        const left = Math.max(0, th - cart.total_price);
        $('[data-ship-text]', bar).textContent = left > 0
          ? `You're ${formatMoney(left)} away from free delivery`
          : 'Free UK delivery unlocked';
        $('[data-ship-fill]', bar).style.width = `${clamp(cart.total_price / th, 0, 1) * 100}%`;
      }
      const body = $('[data-cart-items]', this.el);
      $('.drawer__foot', this.el).hidden = !cart.item_count;
      if (!cart.item_count) {
        body.innerHTML = `<div class="drawer__empty"><p>${D.strings.empty}</p><a class="btn" href="${D.routes.root}collections/all">Shop now</a></div>`;
        return;
      }
      const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
      body.innerHTML = cart.items.map((i) => `
        <div class="line">
          <a class="line__img" href="${esc(i.url)}">${i.image ? `<img src="${esc(i.image + (i.image.includes('?') ? '&' : '?') + 'width=200')}" alt="${esc(i.product_title)}" width="100" height="100" loading="lazy">` : ''}</a>
          <div>
            <a class="line__title" href="${esc(i.url)}">${esc(i.product_title)}</a>
            ${i.variant_title ? `<span class="line__variant">${esc(i.variant_title)}</span>` : ''}
            <div class="qty" role="group" aria-label="Quantity">
              <button type="button" data-line-qty="${i.quantity - 1}" data-key="${esc(i.key)}" aria-label="Decrease">−</button>
              <input value="${i.quantity}" readonly aria-label="Quantity">
              <button type="button" data-line-qty="${i.quantity + 1}" data-key="${esc(i.key)}" aria-label="Increase">+</button>
            </div>
            <div><button class="line__remove" type="button" data-line-qty="0" data-key="${esc(i.key)}">${D.strings.remove}</button></div>
          </div>
          <span class="line__price">${formatMoney(i.final_line_price)}</span>
        </div>`).join('');
      if (motion) gsap.from($$('.line', body), { opacity: 0, x: 30, stagger: .06, duration: .6, ease: 'expo.out' });
    }
  };

  /* ------------------------------------------------------------------
     Newsletter: keep the success message in view
  ------------------------------------------------------------------ */

  /* ------------------------------------------------------------------
     Section registry (works with the theme editor)
  ------------------------------------------------------------------ */
  const registry = {
    hero: initHero,
    marquee: initMarquee,
    scrub: initScrub,
    hscroll: initHScroll,
    vgrid: initVideoGrid,
    finale: initFinale,
    footer: initFooter
  };
  const contexts = new Map();
  const intros = [];

  function initSection(el) {
    const type = el.dataset.sectionType;
    const ctx = motion ? gsap.context(() => {}, el) : null;
    let cleanup = null;
    const run = () => {
      if (registry[type]) {
        const r = registry[type](el);
        if (type === 'hero' && typeof r === 'function') intros.push(r);
        else if (typeof r === 'function') cleanup = r;
      }
      initReveals(el);
      initAutoplay(el);
      initMagnetic(el);
      initTilt(el);
      initFaq(el);
      initProduct(el);
    };
    if (ctx) ctx.add(run); else run();
    contexts.set(el, { ctx, cleanup });
  }
  function destroySection(el) {
    const c = contexts.get(el);
    if (!c) return;
    c.cleanup && c.cleanup();
    c.ctx && c.ctx.revert();
    contexts.delete(el);
  }

  async function boot() {
    initLenis();
    initCursor();
    initHeader();
    Cart.init();
    initMagnetic(document);
    const preload = runPreloader();
    await fontsReady;
    $$('[data-section-type]').forEach(initSection);
    await preload;
    intros.forEach((fn) => fn());
    if (motion) ScrollTrigger.refresh();
    if (Cart.el) Cart.refresh(false).catch(() => {});
  }

  document.addEventListener('shopify:section:load', (e) => {
    const el = e.target.querySelector('[data-section-type]');
    if (el) { initSection(el); const i = intros.pop(); i && i(); motion && ScrollTrigger.refresh(); }
  });
  document.addEventListener('shopify:section:unload', (e) => {
    const el = e.target.querySelector('[data-section-type]');
    if (el) destroySection(el);
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
